import { RecentOrdersResponse } from './data-contracts';
import { HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class RecentOrders<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Retrieves the most recent customer orders including order details and associated product part numbers, used to display order history in the header panel.
	 *
	 * @tags RecentOrders
	 * @name RecentOrders
	 * @summary Get recent orders for header panel
	 * @request GET:/store/{storeId}/hdm/recentOrder/{status}
	 * @secure
	 * @response `200` `RecentOrdersResponse` The request completed successfully.
	 * @response `400` `void` Bad request. One or more parameters are missing or invalid.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` Forbidden. The user is not authorized to access this resource.
	 * @response `404` `void` Resource not found.
	 * @response `500` `void` Internal server error.
	 */
	recentOrders = (
		storeId: string,
		status: string,
		query?: {
			/** @example 1 */
			pageNumber?: number;
			/** @example 5 */
			pageSize?: number;
			/**
			 * Language identifier. If not specified, the "locale" parameter is used. If "locale" is not specified, then the store default language is used.
			 * @example "-1, -2, -3, -4, -5"
			 */
			langId?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (loggerCan('trace') && (!this.http.traceDetails || this.http.traceDetails['recentOrders'])) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/hdm/recentOrder/${status}`,
				params: _params,
				query: query,
				methodName: 'recentOrders',
			};
			logger.trace(traceData);
		}
		return this.http.request<RecentOrdersResponse, void>({
			path: `/store/${storeId}/hdm/recentOrder/${status}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
